import { Clickable, Stack, Text } from '@moda/om';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import ExpandIcon from '@moda/icons/expand-12';
import HideIcon from '@moda/icons/hide-12';
import ChevronRightIcon from '@moda/icons/chevron-right-12';
import { useNavItemHighlighting } from '../../../../hooks/useNavItemHighlighting';

import './RefreshedMobileSiteNavExpandable.scss';

interface Props {
  title: string;
  children: React.ReactNode;
  url?: string;
}

export const RefreshedMobileSiteNavExpandable: React.FC<Props> = ({ title, children, url }) => {
  const [expanded, setExpanded] = useState(false);
  const { color } = useNavItemHighlighting(title || '');

  const hasChildren = useMemo(() => {
    const childrenCount = React.Children.toArray(children).length;

    if (childrenCount > 0) return true;

    return false;
  }, [children]);

  const href = useMemo(() => {
    if (!url) return undefined;

    if (hasChildren) return undefined;

    try {
      // we can log this -- here the url looks like https://modaoperandi.com/clothing/etc and it should be strict pathname
      return new URL(url).pathname;
    } catch (error) {
      return url;
    }
  }, [hasChildren, url]);

  const Icon = useMemo(() => {
    if (!hasChildren) {
      return ChevronRightIcon;
    }

    if (expanded) {
      return HideIcon;
    }

    return ExpandIcon;
  }, [expanded, hasChildren]);

  const handleClick = useCallback(() => {
    hasChildren ? setExpanded(previousValue => !previousValue) : undefined;
  }, [hasChildren]);

  return (
    <Stack className="RefreshedMobileSiteNavExpandable" space={3}>
      <Clickable
        className="RefreshedMobileSiteNavExpandable__title"
        href={href}
        onClick={handleClick}
      >
        <Stack alignItems="center" direction="horizontal" justifyContent="space-between" space={2}>
          <Text style={{ color }}>{title}</Text>

          <div>
            <Icon />
          </div>
        </Stack>
      </Clickable>

      <div
        className={classNames('RefreshedMobileSiteNavExpandable__content', {
          'RefreshedMobileSiteNavExpandable__content--expanded': expanded
        })}
      >
        {children}
      </div>
    </Stack>
  );
};
